import { getToken, getInstance, refreshAccessToken, clearTokens } from '../auth.js';

let isRefreshing = false;
let pendingQueue = [];

function flushQueue(newToken) {
  pendingQueue.forEach((cb) => cb(newToken));
  pendingQueue = [];
}

export class FetchUtils {
  static async fetchWithAuth(url, options = {}, _isRetry = false) {
    const token = getToken();
    const instance = getInstance();

    if (!token || !instance) {
      throw new Error('Not authenticated');
    }

    const headers = {
      ...options.headers,
      ...(options.body ? { 'Content-Type': 'application/json' } : {}),
    };
    if (!headers['Authorization'] && !headers['authorization']) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`https://${instance}.assistgo.de${url}`, {
      ...options,
      headers,
      signal: options.signal,
    });

    if (response.status === 401 && !_isRetry) {
      if (url.includes('user/2fa/')) {
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(errorBody.message || 'Two-factor request failed');
      }

      try {
        const cloned = response.clone();
        const body = await cloned.json();
        if (body?.requiresTwoFactor || body?.requiresTwoFactorSetup) {
          throw new Error('Two-factor authentication required');
        }
      } catch (e) {
        if (e.message.includes('Two-factor')) throw e;
      }

      const newToken = await this._handleTokenRefresh();
      return this.fetchWithAuth(url, options, true);
    }

    if (!response.ok) {
      let details = '';
      try {
        const data = await response.clone().json();
        details = data?.message || data?.error || '';
      } catch (_) {
        try { details = await response.clone().text(); } catch (_) {}
      }
      throw new Error(`API request failed: ${response.status} ${response.statusText}${details ? ` - ${details}` : ''}`);
    }

    return response.json();
  }

  static async _handleTokenRefresh() {
    if (isRefreshing) {
      return new Promise((resolve) => {
        pendingQueue.push(resolve);
      });
    }

    isRefreshing = true;

    try {
      const newToken = await refreshAccessToken();
      flushQueue(newToken);
      return newToken;
    } catch (error) {
      pendingQueue = [];
      clearTokens();
      window.location.reload();
      throw error;
    } finally {
      isRefreshing = false;
    }
  }

  /**
   * Authenticated fetch returning the raw Response (for SSE streams).
   * Does not parse JSON; caller handles status and body.
   */
  static async fetchRaw(url, options = {}, _isRetry = false) {
    const token = getToken();
    const instance = getInstance();

    if (!token || !instance) {
      throw new Error('Not authenticated');
    }

    const headers = {
      ...options.headers,
      ...(options.body ? { 'Content-Type': 'application/json' } : {}),
    };
    if (!headers['Authorization'] && !headers['authorization']) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`https://${instance}.assistgo.de${url}`, {
      ...options,
      headers,
      signal: options.signal,
    });

    if (response.status === 401 && !_isRetry) {
      if (url.includes('user/2fa/')) {
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(errorBody.message || 'Two-factor request failed');
      }

      try {
        const cloned = response.clone();
        const body = await cloned.json();
        if (body?.requiresTwoFactor || body?.requiresTwoFactorSetup) {
          throw new Error('Two-factor authentication required');
        }
      } catch (e) {
        if (e.message?.includes('Two-factor')) throw e;
      }

      await this._handleTokenRefresh();
      return this.fetchRaw(url, options, true);
    }

    return response;
  }

  static async post(url, data = {}, signal = null) {
    return this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(data),
      signal: signal,
    });
  }

  static async put(url, data = {}) {
    return this.fetchWithAuth(url, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  static async get(url) {
    const result = await this.fetchWithAuth(url, {
      method: 'GET',
    });
    return result;
  }

  static async patch(url, data = {}) {
    return this.fetchWithAuth(url, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  static async delete(url) {
    return this.fetchWithAuth(url, {
      method: 'DELETE',
    });
  }
}
